'use client';

import type { CSSProperties, ReactNode } from 'react';
import {
  DEFAULT_SHELTER_PALETTE_ID,
  isKnownShelterPaletteId,
  normalizeAdoptionQuizThemeMode,
  resolveAdoptionQuizThemeCssVars,
  type AdoptionQuizThemeMode,
  type ShelterPaletteId,
} from '../../../theme/shelterPaletteCatalog';
import { AdoptShelterQuizThemeVarsProvider } from './AdoptShelterQuizThemeVarsContext';

const ADOPT_QUIZ_THEME_ROOT_CLASS =
  'adopt-public-quiz-theme-root flex min-h-full w-full flex-1 flex-col bg-background text-foreground';

function cssVarsToStyle(vars: Record<string, string>): CSSProperties {
  return vars as CSSProperties;
}

export type PublicAdoptQuizThemeInput = {
  paletteId: string;
  mode: AdoptionQuizThemeMode;
} | null;

function resolveThemeVars(theme: PublicAdoptQuizThemeInput) {
  const paletteId: ShelterPaletteId = isKnownShelterPaletteId(theme?.paletteId)
    ? theme.paletteId
    : DEFAULT_SHELTER_PALETTE_ID;
  const modeNorm = normalizeAdoptionQuizThemeMode(theme?.mode);
  const mode: AdoptionQuizThemeMode = modeNorm ?? 'light';
  const vars = resolveAdoptionQuizThemeCssVars(paletteId, mode);
  return { paletteId, mode, vars };
}

/**
 * Applies catalog `--theme-*` on a subtree. Sets `data-app-theme` so `globals.css`
 * re-binds Tailwind `--color-*` to these tokens (see `[data-app-theme]` bridge); without it,
 * `bg-background` / `text-foreground` keep inheriting the resolved public palette from `:root`.
 */
export function AdoptPublicQuizThemeRoot({
  theme,
  children,
}: {
  theme: PublicAdoptQuizThemeInput;
  children: ReactNode;
}) {
  const { vars } = resolveThemeVars(theme);

  return (
    <AdoptShelterQuizThemeVarsProvider value={vars}>
      <div
        className={ADOPT_QUIZ_THEME_ROOT_CLASS}
        data-app-theme='adopt-quiz'
        style={cssVarsToStyle(vars)}
      >
        {children}
      </div>
    </AdoptShelterQuizThemeVarsProvider>
  );
}
